import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  useToast,
} from '@eluve/components';

import { deletePromptTemplateDefaultMutation } from './promptTemplateDefaults.operations';

interface DeletePromptTemplateDefaultDialogProps {
  defaultId: string;
  userId?: string | null;
}

export const DeletePromptTemplateDefaultDialog: React.FC<
  DeletePromptTemplateDefaultDialogProps
> = ({ defaultId, userId }) => {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const [deleteDefault, { loading }] = useMutation(
    deletePromptTemplateDefaultMutation,
    {
      variables: { id: defaultId },
      optimisticResponse: {
        deletePromptTemplateDefaultsByPk: {
          __typename: 'PromptTemplateDefaults' as const,
          id: defaultId,
        },
      },
      update: (cache) => {
        cacheUtils.evict({
          typeName: 'PromptTemplateDefaults',
          key: { id: defaultId },
        });

        // Decrement the user's override count in cache
        if (userId) {
          cache.modify({
            id: cache.identify({ __typename: 'Users', id: userId }),
            fields: {
              promptTemplateDefaultsAggregate(existing) {
                const currentCount = existing?.aggregate?.count ?? 1;
                return {
                  ...existing,
                  aggregate: {
                    ...existing?.aggregate,
                    count: Math.max(0, currentCount - 1),
                  },
                };
              },
            },
          });
        }

        cache.gc();
      },
      onError: (error) => {
        toast.error(`Failed to delete default: ${error.message}`);
      },
      onCompleted: () => {
        setOpen(false);
      },
    },
  );

  const handleDelete = async () => {
    await deleteDefault();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Delete
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt Template Default</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this prompt template default? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              className="bg-bg-negative-strong-primary hover:bg-bg-negative-strong-primary/80"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
